// src/pages/ElectivosSelection.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import { saveAs } from 'file-saver';
import './ElectivosSelection.css';

export default function ElectivosSelection() {
  const [electivos, setElectivos] = useState([]);
  const [previousElectives, setPrevious] = useState([]);
  const [primaria, setPrimaria] = useState([null, null, null]);
  const [secundaria, setSecundaria] = useState([null, null, null]);
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState('success'); // 'success' o 'error'
  const [tomaEnabled, setTomaEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [studentName, setStudentName] = useState('');

  const API = process.env.REACT_APP_API_URL || '';
  const token = localStorage.getItem('token');

  // Mapeo para mensajes de error más claros
  const areaFullNames = {
    'LCS': 'Lenguaje y Ciencias Sociales',
    'CNM': 'Ciencias Naturales y Matemáticas',
    'AEF': 'Artes y Educación Física'
  };

  // Captura 401 para sesión expirada
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 401) {
          setMsgType('error');
          setMsg('Tu sesión expiró. Por favor vuelve a iniciar sesión.');
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  // Carga inicial de datos: configuración, perfil, disponibles y guardados
  useEffect(() => {
    (async () => {
      try {
        const config = { headers: { Authorization: `Bearer ${token}` } };
        
        const [flagRes, meRes, dispRes, savedRes] = await Promise.all([
          axios.get(`${API}/student/settings/toma_ramos`, config),
          axios.get(`${API}/student/me`, config),
          axios.get(`${API}/student/electivos-disponibles`, config),
          axios.get(`${API}/student/electivos-saved`, config)
        ]);

        setTomaEnabled(flagRes.data.enabled);
        setStudentName(meRes.data.nombre || '');

        const prevs = [
          meRes.data.previous_elective_1_id,
          meRes.data.previous_elective_2_id,
          meRes.data.previous_elective_3_id
        ].filter(Boolean);
        setPrevious(prevs);

        // Procesar electivos disponibles
        const map = new Map(
          dispRes.data.map(el => [`${el.codigo}::${el.bloque}`, el])
        );
        setElectivos(
          Array.from(map.values()).map(el => ({
            ...el,
            bloque: Number(el.bloque)
          }))
        );

        // Cargar elecciones guardadas directamente
        if (savedRes.data.primary && savedRes.data.primary.length > 0) {
          setPrimaria(savedRes.data.primary);
        }
        if (savedRes.data.secondary && savedRes.data.secondary.length > 0) {
          setSecundaria(savedRes.data.secondary);
        }

      } catch (e) {
        console.error('Error cargando datos iniciales:', e);
        setMsgType('error');
        setMsg('Error cargando datos desde el servidor');
      } finally {
        setLoading(false);
      }
    })();
  }, [API, token]);

  const handleChange = (idx, valor, tipo) => {
    const setter = tipo === 'primaria' ? setPrimaria : setSecundaria;
    setter(arr => {
      const copia = [...arr];
      copia[idx] = valor || null;
      return copia;
    });
  };

  function generatePdf(primaria, secundaria) {
    const doc = new jsPDF();
    const now = new Date().toLocaleString();
    doc.setFontSize(16);
    doc.text('Comprobante de Elecciones 2026', 20, 20);
    doc.setFontSize(12);
    doc.text(`Estudiante: ${studentName}`, 20, 30);
    doc.text(`Fecha y hora: ${now}`, 20, 40);

    let y = 60;
    for (let i = 0; i < 3; i++) {
      const codePri = primaria[i];
      const codeSec = secundaria[i];
      const namePri = electivos.find(e => e.codigo === codePri)?.nombre || codePri || '—';
      const nameSec = electivos.find(e => e.codigo === codeSec)?.nombre || codeSec || '—';
      doc.text(`Bloque ${i + 1}: Principal: ${namePri} / Secundario: ${nameSec}`, 20, y);
      y += 10;
    }

    return doc.output('blob');
  }

  async function emailPdf(pdfBlob) {
    const form = new FormData();
    form.append('comprobante', pdfBlob, 'comprobante.pdf');
    await axios.post(
      `${API}/student/electivos/comprobante`,
      form,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      }
    );
  }

  const handleSubmit = async e => {
    e.preventDefault();
    if (!tomaEnabled) return;
    setMsg('');

    // 1) Validación: No repetir mismo electivo en el mismo bloque
    for (let i = 0; i < 3; i++) {
      if (primaria[i] && primaria[i] === secundaria[i]) {
        setMsgType('error');
        return setMsg(`Bloque ${i + 1}: No puedes elegir el mismo electivo como principal y secundario.`);
      }
    }

    // 2) Validación: Máximo 2 por área (Solo opciones principales)
    const cuentas = {};
    primaria.forEach(codigo => {
      if (!codigo) return;
      const el = electivos.find(e => e.codigo === codigo);
      const area = el?.area;
      if (area) cuentas[area] = (cuentas[area] || 0) + 1;
    });

    for (const [area, cnt] of Object.entries(cuentas)) {
      if (cnt > 2) {
        setMsgType('error');
        const nombreArea = areaFullNames[area] || area;
        return setMsg(`Límite de área: Has seleccionado ${cnt} ramos de "${nombreArea}". El máximo permitido es 2.`);
      }
    }

    try {
      // Guardar elecciones
      await axios.post(
        `${API}/student/electivos`,
        {
          electivos_primary: primaria,
          electivos_secondary: secundaria
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMsgType('success');
      setMsg('Elección guardada con éxito ✅');

      // Generar y enviar PDF
      const pdfBlob = generatePdf(primaria, secundaria);
      saveAs(pdfBlob, 'comprobante_elecciones.pdf');

      await emailPdf(pdfBlob);
      setMsg(prev => prev + ' Comprobante enviado por email 📧');
    } catch (err) {
      console.error('Error al guardar:', err);
      setMsgType('error');
      setMsg('Error al guardar o generar el comprobante');
    }
  };

  if (loading) {
    return <div className="loading-container"><p>Cargando selección de electivos…</p></div>;
  }

  const bloques = [1, 2, 3];

  return (
    <div className="electivos-page">
      <h2>Selección de Electivos 2026</h2>

      <div className="nomenclatura">
        <strong>ÁREAS ACADÉMICAS</strong><br/>
        LCS: {areaFullNames['LCS']} | CNM: {areaFullNames['CNM']} | AEF: {areaFullNames['AEF']}
      </div>

      {!tomaEnabled && (
        <p className="message warning">
          La toma de ramos está actualmente deshabilitada por administración.
        </p>
      )}
      
      {msg && (
        <p className={`message ${msgType}`}>
          {msg}
        </p>
      )}

      <form onSubmit={handleSubmit}>
        {bloques.map(b => {
          const idx = b - 1;
          return (
            <div key={b} className="bloque-group">
              <div className="bloque-titulo">Bloque {b}</div>

              <label>Opción Principal</label>
              <select
                disabled={!tomaEnabled}
                value={primaria[idx] || ''}
                onChange={e => handleChange(idx, e.target.value, 'primaria')}
                required
              >
                <option value="">-- Seleccione Principal --</option>
                {electivos
                  .filter(el => el.bloque === b)
                  .filter(el => !previousElectives.includes(el.codigo))
                  .map(el => (
                    <option key={`p-${b}-${el.codigo}`} value={el.codigo}>
                      {el.nombre} ({el.area})
                    </option>
                  ))}
                {/* Fallback visual para ramos ya guardados que no están en disponibles */}
                {primaria[idx] && !electivos.find(e => e.codigo === primaria[idx]) && (
                   <option value={primaria[idx]}>{primaria[idx]} (Asignado)</option>
                )}
              </select>

              <label>Opción Secundaria</label>
              <select
                disabled={!tomaEnabled}
                value={secundaria[idx] || ''}
                onChange={e => handleChange(idx, e.target.value, 'secundaria')}
                required
              >
                <option value="">-- Seleccione Secundaria --</option>
                {electivos
                  .filter(el => el.bloque === b)
                  .filter(el => !previousElectives.includes(el.codigo))
                  .filter(el => el.codigo !== primaria[idx])
                  .map(el => (
                    <option key={`s-${b}-${el.codigo}`} value={el.codigo}>
                      {el.nombre} ({el.area})
                    </option>
                  ))}
                {secundaria[idx] && !electivos.find(e => e.codigo === secundaria[idx]) && (
                   <option value={secundaria[idx]}>{secundaria[idx]} (Asignado)</option>
                )}
              </select>
            </div>
          );
        })}

        <button
          type="submit"
          className="btn-guardar"
          disabled={!tomaEnabled}
        >
          Guardar elecciones y descargar PDF
        </button>
      </form>
    </div>
  );
}